<template>
  <div
    id="ttm-background"
    :class="backgroundClasses"
    @click="clickBackground"
    role="presentation"
    data-testid="ttm-background"
  >
    <div id="ttm-modal" :class="modalClasses" @click="clickForeground" role="dialog" aria-labelledby="ttm-title">
      <button type="button" id="ttm-close-button" @click="close" aria-label="close">
        <img :src="cancelImg" role="presentation" />
      </button>
      <div id="ttm-copy">
        <h2 id="ttm-title">{{ title }}</h2>
        <p id="ttm-description">{{ description }}</p>
      </div>
      <div id="ttm-categories">
        <a
          class="ttm-treatment-link"
          v-for="(treatment, uid) in treatmentOptions"
          :key="uid"
          :href="consultationLink(uid)"
          :aria-labelledby="treatmentLinkId(uid)"
        >
          <div class="ttm-tl-left" role="presentation">
            <h3 :id="treatmentLinkId(uid)" class="ttm-treatment-name">{{ treatment.name }}</h3>
            <p class="ttm-treatment-description">{{ treatment.description }}</p>
          </div>
          <div class="ttm-spacer" role="presentation" />
          <div class="ttm-tl-right" role="presentation">
            <img :src="chevronRight" aria-label="A right arrow" />
          </div>
        </a>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { TreatmentOptions } from "@/MedExpressJS/Categories";
import cancelImg from "./assets/cancel.png";
import chevronRight from "./assets/chevron_right.png";

export default Vue.extend({
  props: {
    title: { type: String, required: true },
    description: { type: String, required: true },
    treatmentOptions: { type: Object as PropType<TreatmentOptions>, required: true },
    visible: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      cancelImg: cancelImg,
      chevronRight: chevronRight,
    };
  },
  methods: {
    clickForeground(e: MouseEvent): void {
      e.stopPropagation();
    },
    clickBackground(e: MouseEvent): void {
      e.stopPropagation();
      this.close();
    },
    close(): void {
      this.$emit("close");
    },
    consultationLink(uid: string) {
      return this.treatmentOptions[uid].consultationURI;
    },
    treatmentLinkId(uid: string) {
      return `ttm-treament-name-${uid}`;
    },
  },
  computed: {
    modalClasses(): { [classname: string]: boolean } {
      return {
        visible: this.visible,
      };
    },
    backgroundClasses(): { [classname: string]: boolean } {
      return {
        "ttm-background": true,
        "visible": this.visible,
      };
    },
  },
});
</script>

<style scoped lang="scss">
$breakpoint: 768px;

#ttm-background {
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0);
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  visibility: hidden;

  &.visible {
    background-color: rgba(0, 0, 0, 0.25);
    visibility: visible;
  }

  transition: background-color 0.3s ease-out 0s, visibility 0.3s ease-out 0s;
}

#ttm-close-button {
  position: absolute;
  top: 19px;
  right: 19px;

  @media (min-width: ($breakpoint + 1px)) {
    top: 28px;
    right: 28px;
  }

  @media (max-width: $breakpoint) {
    top: 19px;
    right: 19px;
  }

  background: none;
  border: none;
}

#ttm-modal {
  z-index: 1001;
  background-color: white;
  position: absolute;

  cursor: default;

  @media (min-width: ($breakpoint + 1px)) {
    height: 100%;
    width: 514px;
    padding: 84px 40px;
    border-radius: 24px 0px 0px 0px;
    right: 0;
    top: 0;
    transform: translateX(100%);

    &.visible {
      transform: translateX(0);
    }
  }

  @media (max-width: $breakpoint) {
    padding: 24px;
    bottom: 0;
    left: 0;
    width: 100%;
    border-radius: 24px 24px 0px 0px;
    transform: translateY(100%);

    &.visible {
      transform: translateY(0);
    }
  }

  transition: transform 0.3s ease-out 0s;

  #ttm-copy {
    margin-bottom: 32px;

    #ttm-title {
      display: block;
      font-size: 24px;
      font-weight: 700;
      color: #003f82;
    }

    #ttm-description {
      display: block;
      font-size: 16px;
      font-weight: 400;
      padding: 4px 0;
      color: #003f82;
      margin-bottom: 32px;
    }
  }

  .ttm-treatment-link {
    display: flex;
    margin-bottom: 16px;
    padding: 16px;
    background-color: #5baadb;
    border-radius: 12px;
    border: none;
    width: 100%;

    flex-direction: row;
    justify-content: space-between;
    align-items: center;

    &:hover {
      text-decoration: none;
    }

    .ttm-spacer {
      min-width: 16px;
    }

    .ttm-tl-left {
      display: block;
      color: white;
      text-align: left;

      > h3 {
        font-size: 18px;
        font-weight: 700;
        margin: 0 0 4px 0;
      }

      > p {
        display: block;
        color: white;
        text-align: left;
        font-size: 13px;
        font-weight: 400;
        margin: 0;
      }
    }

    .ttm-tl-right {
      margin-right: 4px;
    }
  }
}
</style>
